import {
  IconButton,
  IconButtonProps,
  Tooltip,
  TooltipProps,
} from '@chakra-ui/react';
import { useState } from 'react';
import { TbClipboard, TbClipboardCheck } from 'react-icons/tb';

export interface CopyButtonProps extends Omit<
  IconButtonProps,
  'aria-label' | 'icon' | 'onClick'
> {
  textToCopy: string;
  copyTooltipLabel?: string;
  tooltipProps?: Partial<TooltipProps>;
}

const DEFAULT_COPY_TOOLTIP_LABEL = 'Click to Copy';

/**
 * CopyButton component to copy text to the clipboard. Shows an icon with
 * a tooltip that says "Click to Copy" when the user hovers over it.
 * When the user clicks the icon, the text is copied to the clipboard and
 * the tooltip says "Copied!".
 * @param textToCopy - The text to copy to the clipboard.
 * @param copyTooltipLabel - The label to show in the tooltip.
 * @param ...iconProps - The props to pass to the IconButton component. These will override the default props.
 * @returns A CopyButton component.`
 */
export const CopyButton: React.FC<CopyButtonProps> = ({
  textToCopy,
  copyTooltipLabel = DEFAULT_COPY_TOOLTIP_LABEL,
  tooltipProps,
  ...iconButtonProps
}) => {
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setHasCopied(true);
      // Reset the "Copied" state after 2 seconds
      setTimeout(() => {
        setHasCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  return (
    <Tooltip
      label={hasCopied ? 'Copied!' : copyTooltipLabel}
      closeOnClick={false}
      borderRadius="4px"
      placement="top"
      closeDelay={200}
      hasArrow
      {...tooltipProps}
    >
      <IconButton
        size="1rem"
        ml={1}
        color="text.muted"
        variant="ghost"
        aria-label="Copy to Clipboard"
        icon={hasCopied ? <TbClipboardCheck /> : <TbClipboard />}
        onClick={handleCopy}
        {...iconButtonProps}
      />
    </Tooltip>
  );
};

CopyButton.displayName = 'CopyButton';
